// src/interfaces/manager/MaterialsManager.js
import React, { useState, useEffect } from 'react';

const MaterialsManager = () => {
  const [materialData, setMaterialData] = useState({
    jobTypes: [],
    vinylMaterials: [],
    boardMaterials: [],
    bannerMaterials: [],
    posterMaterials: [],
    laminateMaterials: [],
    finishingProcesses: [],
    printMachines: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('jobTypes');
  
    // API URL
   const API_URL = process.env.REACT_APP_API_URL || '';
  
  // Fetch all pricing data on component mount
  useEffect(() => {
    fetchAllPricingData();
  }, []);
  
  // Fetch all pricing data from API
  const fetchAllPricingData = async () => {
    setIsLoading(true);
    try {
      // Fetch job types
      const jobTypesResponse = await fetch(`${API_URL}/api/job-types`);
      if (!jobTypesResponse.ok) throw new Error('Failed to fetch job types');
      const jobTypesData = await jobTypesResponse.json();
      
      // Fetch vinyl materials
      const vinylResponse = await fetch(`${API_URL}/api/materials/vinyl`);
      if (!vinylResponse.ok) throw new Error('Failed to fetch vinyl materials');
      const vinylData = await vinylResponse.json();
      
      // Fetch board materials
      const boardResponse = await fetch(`${API_URL}/api/materials/board`);
      if (!boardResponse.ok) throw new Error('Failed to fetch board materials');
      const boardData = await boardResponse.json();
      
      // Fetch banner materials
      const bannerResponse = await fetch(`${API_URL}/api/materials/banner`);
      if (!bannerResponse.ok) throw new Error('Failed to fetch banner materials');
      const bannerData = await bannerResponse.json();
      
      // Fetch poster materials
      const posterResponse = await fetch(`${API_URL}/api/materials/poster`);
      if (!posterResponse.ok) throw new Error('Failed to fetch poster materials');
      const posterData = await posterResponse.json();
      
      // Fetch laminates
      const laminateResponse = await fetch(`${API_URL}/api/materials/laminate`);
      if (!laminateResponse.ok) throw new Error('Failed to fetch laminates');
      const laminateData = await laminateResponse.json();
      
      // Fetch finishing processes
      const finishResponse = await fetch(`${API_URL}/api/finishing-processes`);
      if (!finishResponse.ok) throw new Error('Failed to fetch finishing processes');
      const finishData = await finishResponse.json();
      
      // Fetch print machines
      const machineResponse = await fetch(`${API_URL}/api/print-machines`);
      if (!machineResponse.ok) throw new Error('Failed to fetch print machines');
      const machineData = await machineResponse.json();
      
      setMaterialData({
        jobTypes: jobTypesData,
        vinylMaterials: vinylData,
        boardMaterials: boardData,
        bannerMaterials: bannerData,
        posterMaterials: posterData,
        laminateMaterials: laminateData,
        finishingProcesses: finishData,
        printMachines: machineData
      });
      
      setError(null);
    } catch (err) {
      console.error('Error fetching materials:', err);
      setError('Failed to load pricing data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  

  // Tab categories
  const categories = [
    { key: 'jobTypes', name: 'Job Types' },
    { key: 'vinylMaterials', name: 'Vinyl Materials' },
    { key: 'boardMaterials', name: 'Board Materials' },
    { key: 'bannerMaterials', name: 'Banner Materials' },
    { key: 'posterMaterials', name: 'Poster Materials' },
    { key: 'laminateMaterials', name: 'Laminates' },
    { key: 'finishingProcesses', name: 'Finishing Processes' },
    { key: 'printMachines', name: 'Print Machines' }
  ];

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'jobTypes':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materialData.jobTypes.length > 0 ? (
                  materialData.jobTypes.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="1" className="px-6 py-4 text-center text-sm text-gray-500">
                      No job types found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      
      case 'vinylMaterials':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Width (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Roll Length (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price/Unit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Markup %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materialData.vinylMaterials.length > 0 ? (
                  materialData.vinylMaterials.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.details}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.width}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.height || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{item.price_per_unit.toFixed(2)}/{item.unit_type === 'sqm' ? 'sqm' : 'lm'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.markup_percent}%</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No vinyl materials found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      
      case 'boardMaterials':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Width (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Height (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price/Unit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Markup %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materialData.boardMaterials.length > 0 ? (
                  materialData.boardMaterials.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.details}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.width}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.height}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{item.price_per_unit.toFixed(2)}/{item.unit_type === 'sqm' ? 'sqm' : 'lm'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.markup_percent}%</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No board materials found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      
      case 'bannerMaterials':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Width (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Roll Length (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price/Unit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Markup %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materialData.bannerMaterials.length > 0 ? (
                  materialData.bannerMaterials.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.details}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.width}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.height || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{item.price_per_unit.toFixed(2)}/{item.unit_type === 'sqm' ? 'sqm' : 'lm'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.markup_percent}%</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No banner materials found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      
      case 'posterMaterials':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Width (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Roll Length (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price/Unit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Markup %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materialData.posterMaterials.length > 0 ? (
                  materialData.posterMaterials.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.details}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.width}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.height || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{item.price_per_unit.toFixed(2)}/{item.unit_type === 'sqm' ? 'sqm' : 'lm'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.markup_percent}%</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No poster materials found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      
      case 'laminateMaterials':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Width (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Roll Length (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price/Unit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Markup %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materialData.laminateMaterials.length > 0 ? (
                  materialData.laminateMaterials.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.details}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.width}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.height || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{item.price_per_unit.toFixed(2)}/{item.unit_type === 'sqm' ? 'sqm' : 'lm'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.markup_percent}%</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No laminate materials found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      
      case 'finishingProcesses':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Process
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Setup Cost
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Run-on Cost
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materialData.finishingProcesses.length > 0 ? (
                  materialData.finishingProcesses.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{item.setup_cost.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{(item.run_on_cost ?? 0).toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.unit}</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                      No finishing processes found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      
      case 'printMachines':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Machine
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Max Width (m)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Setup Cost
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost per sq.m
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materialData.printMachines.length > 0 ? (
                  materialData.printMachines.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.max_width}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{item.setup_cost.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">£{item.cost_per_sqm.toFixed(2)}</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                      No print machines found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      
      default:
        return <div>Select a category</div>;
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Materials and Pricing Management</h2>
        
        
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold mb-4">Current Pricing Data</h3>
          
          {isLoading ? (
            <div className="text-center py-4">
              <p>Loading pricing data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-100 text-red-800 p-3 rounded">
              {error}
            </div>
          ) : (
            <div>
              {/* Tab navigation */}
              <div className="flex space-x-1 rounded-xl bg-blue-900/10 p-1 mb-4 overflow-x-auto">
                {categories.map((category) => (
                  <button
                    key={category.key}
                    onClick={() => setActiveTab(category.key)}
                    className={`px-3 py-2 whitespace-nowrap text-sm font-medium leading-5 text-blue-700 rounded-lg
                    ${
                      activeTab === category.key
                        ? 'bg-white shadow'
                        : 'text-blue-600 hover:bg-white/[0.12] hover:text-blue-700'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
              
              {/* Tab content */}
              <div className="mt-2 rounded-xl bg-white p-3">
                {renderTabContent()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialsManager;
